import { useMemo, useState } from 'react';
import { ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

// Shared shell for the two live agent views.
//
// Both screens answer the same question — "has this machine checked in yet?" —
// so they share a layout rather than drifting apart. What differs is the
// columns, which each screen supplies.
//
// The count and the fetched-at line matter as much as the table: somebody
// enrolling a laptop needs to know the number they are looking at was fetched
// just now, not at 05:30 with the nightly sync.

export const fmtWhen = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

export function Pill({ ok, children }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ok
      ? 'bg-emerald-100 text-emerald-800'
      : 'bg-gray-100 text-gray-600'}`}
    >
      {children}
    </span>
  );
}

export default function AgentList({
  title, blurb, data, isLoading, isFetching, error, refetch, stats, columns, searchFields,
}) {
  const [q, setQ] = useState('');
  // Memoised: `data?.agents || []` builds a fresh array on every render, which
  // would change the filter's dependency each time and re-run it for nothing.
  const agents = useMemo(() => data?.agents || [], [data]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return agents;
    return agents.filter((a) => searchFields.some(
      (f) => String(a[f] || '').toLowerCase().includes(needle)));
  }, [agents, q, searchFields]);

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">{blurb}</p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          {isFetching ? 'Checking…' : 'Refresh'}
        </button>
      </div>

      {error ? (
        <div className="mt-4 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Could not reach it: {error?.response?.data?.error || error.message}
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {(stats || []).map((s) => (
          <div key={s.label} className="rounded-lg border border-gray-200 bg-white px-4 py-2">
            <div className="text-xl font-bold text-gray-900 tabular-nums">{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
        <div className="ml-auto text-xs text-gray-400">
          {data?.fetched_at ? `checked ${fmtWhen(data.fetched_at)}` : ''}
        </div>
      </div>

      <div className="relative mt-4 max-w-sm">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, serial, user…"
          className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </div>

      <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>{columns.map((c) => <th key={c.key} className="px-4 py-2.5">{c.label}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
            ) : shown.map((a, i) => (
              <tr key={a.id || a.serial || a.hostname || i} className="hover:bg-gray-50">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-2.5 text-gray-700">{c.render(a)}</td>
                ))}
              </tr>
            ))}
            {!isLoading && !shown.length ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-400">
                  {agents.length ? 'Nothing matches that search.' : 'No agents reported.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
